import React, { useEffect, useState } from 'react';
import { Button, Divider, Grid } from '@mui/material';
import { Edit, Map } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Strings from '../../i18n';
import { ButtonStyledText, ContainerArrow } from './applicationsViewStyles,';
import { ApplicationsDetails } from '../../templates/applications/applicationDetails/applicationDetails';
import { ApplicationsTemplate } from '../../templates/applications/applicationsTemplate';
import { RightSideBar } from '../../components/layout/rightSideBar/rightSideBar';
import { viewRightSideBarEnum } from '../../constants/sidebarEnums';
import { TransferLocationBar } from '../../templates/locations/transferLocationBar/transferLocationBar';
import { ShareLocationBar } from '../../templates/locations/shareLocationBar/shareLocationBar';
import { LocationByUser } from '../../types/location/location';
import { ItemDetail } from '../../components/itemDetail/itemDetail';
import { locationDetails } from '../../store/slices/locations/location';
import { StyledTitleHeader } from '../../components/layout/main/mainStyles';
import { ChangeLatLongLocationBar } from '../../templates/locations/changeLatLongLocation/changeLatLongLocation';
import { ChangeLocationNameBar } from '../../templates/locations/changeLocationName/changeLocationName';
import { ChangeLocationType } from '../../templates/locations/changeLocationType/changeLocationType';
import {
  useDeleteLocationByRoleMutation,
  useDeleteLocationMutation,
  useMakeDefaultLocationMutation,
  useResetLocationMutation
} from '../../services/locations/locationsService';

export const ApplicationsView = () => {
  const { locationId } = useParams();
  const [openRightSideBar, setOpenRightSideBar] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  const locationSelected = useSelector(locationDetails);

  const [makeDefaultLocation, { isSuccess: isSuccessMakeDefaultLocation }] =
    useMakeDefaultLocationMutation();

  const [
    deleteLocationByRole,
    {
      isSuccess: isSuccessDeleteLocationByRole,
      isError: isErrorDeleteLocationByRole,
      error: errorDeleteLocationByRole
    }
  ] = useDeleteLocationByRoleMutation();

  useEffect(() => {
    if (isErrorDeleteLocationByRole && errorDeleteLocationByRole) {
      if (
        'status' in errorDeleteLocationByRole &&
        'data' in errorDeleteLocationByRole
      ) {
        const errorData = errorDeleteLocationByRole.data as {
          message?: string;
        };
        toast.error(errorData.message || Strings.common.somethingWentWrong);
      } else {
        toast.error(Strings.common.somethingWentWrong);
      }
    }
  }, [isErrorDeleteLocationByRole, errorDeleteLocationByRole]);

  const [resetLocation, { isSuccess: isSuccessResetLocation, isError }] =
    useResetLocationMutation();

  const [location] = useState<LocationByUser>({
    locationId: locationId ? parseInt(locationId, 10) : 0,
    locationName: '',
    isOwner: false,
    locationRoleId: 0,
    organizationName: '',
    locationType: 0,
    hasNotes: false
  });
  const [sideBarOpenFor, setSideBarOpenFor] = useState(
    viewRightSideBarEnum.shareLocation
  );

  const openRightSideBarForMethod = (method: number) => {
    setSideBarOpenFor(method);
    setOpenRightSideBar(true);
  };

  const reloadTable = () => {
    setOpenRightSideBar(false);
    window.location.reload();
  };
  const switchComponentToShowRightSideBar = () => {
    switch (sideBarOpenFor) {
      case viewRightSideBarEnum.transferLocation:
        return (
          <TransferLocationBar
            reloadTable={() => {}}
            selectedLocations={[location.locationId]}
            setOpenRightSideBar={setOpenRightSideBar}
          />
        );
      case viewRightSideBarEnum.shareLocation:
        return (
          <ShareLocationBar
            reloadTable={() => {}}
            selectedLocations={[location.locationId]}
            setOpenRightSideBar={setOpenRightSideBar}
          />
        );

      case viewRightSideBarEnum.changeLatLongLocation:
        return (
          <ChangeLatLongLocationBar
            reloadTable={() => reloadTable()}
            location={{
              locationId: location.locationId,
              lat: locationSelected?.geoInfo?.latitude || 0,
              lon: locationSelected?.geoInfo?.longitude || 0
            }}
            setOpenRightSideBar={setOpenRightSideBar}
          />
        );
      case viewRightSideBarEnum.changeLocationName:
        return (
          <ChangeLocationNameBar
            reloadTable={() => reloadTable()}
            location={{
              locationId: location.locationId,
              ownerId: locationSelected?.ownerId || 0,
              name: locationSelected?.name || '',
              geoInfo: {
                latitude: locationSelected?.geoInfo?.latitude || 0,
                longitude: locationSelected?.geoInfo?.longitude || 0,
                address: locationSelected?.geoInfo?.address || ''
              }
            }}
            setOpenRightSideBar={setOpenRightSideBar}
          />
        );
      case viewRightSideBarEnum.changeLocationType:
        return (
          <ChangeLocationType
            selectedLocation={{
              locationId: location.locationId,
              locationType: locationSelected?.locationType || 0,
              name: locationSelected?.name || ''
            }}
            setOpenRightSideBar={setOpenRightSideBar}
            reloadTable={() => reloadTable()}
          />
        );
      default:
        return (
          <ShareLocationBar
            reloadTable={() => {}}
            selectedLocations={[location.locationId]}
            setOpenRightSideBar={setOpenRightSideBar}
          />
        );
    }
  };

  const makeDefaultLocationHandler = () => {
    makeDefaultLocation({
      isOwner: true,
      ownerId: locationSelected?.ownerId || 0,
      locationId: location.locationId
    });
  };

  const deleteLocationHandler = () => {
    if (locationSelected?.isOwner && locationSelected?.hasDevices) {
      toast.error(Strings.locations.removeLocationErrorMessage);
      return;
    }
    deleteLocationByRole([
      { locationId: location.locationId, userId: locationSelected?.ownerId }
    ]);
  };

  const resetLocationHandler = () => {
    resetLocation([location.locationId.toString()]);
  };

  useEffect(() => {
    if (isSuccessMakeDefaultLocation) {
      toast.success(Strings.applications.successMakeDefault);
    }
  }, [isSuccessMakeDefaultLocation]);

  useEffect(() => {
    if (isSuccessDeleteLocationByRole) {
      toast.success(Strings.locations.successDeleteLocations);
      setTimeout(() => {
        navigate('/locations');
      }, 1000);
    }
  }, [isSuccessDeleteLocationByRole]);

  useEffect(() => {
    if (isSuccessResetLocation) {
      toast.success(Strings.locations.resetLocationSuccess);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }, [isSuccessResetLocation]);

  useEffect(() => {
    if (isError) {
      toast.error(Strings.locations.resetLocationError);
    }
  }, [isError]);

  return (
    <div>
      <StyledTitleHeader>{Strings.applications.pageTitle}</StyledTitleHeader>
      <Divider variant="fullWidth" />
      <ApplicationsTemplate />
      <Grid container justifyContent="center" paddingLeft="17%" marginTop={2}>
        <ItemDetail
          nameProperty={Strings.locations.locationName}
          component={
            <Button
              variant="text"
              color="info"
              endIcon={<Edit />}
              size="small"
              onClick={() => {
                openRightSideBarForMethod(
                  viewRightSideBarEnum.changeLocationName
                );
              }}
            >
              {locationSelected?.name}
            </Button>
          }
        />
        <ItemDetail
          nameProperty={Strings.applications.streetAddress}
          component={
            <Button
              variant="text"
              color="info"
              endIcon={<Map />}
              size="small"
              onClick={() => {
                window.open(
                  `https://www.google.com/maps/search/${locationSelected?.geoInfo?.latitude},${locationSelected?.geoInfo?.longitude}`,
                  '_blank'
                );
              }}
            >
              {locationSelected?.geoInfo
                ? locationSelected?.geoInfo.address
                : ''}
            </Button>
          }
        />
        <ItemDetail
          nameProperty={Strings.applications.currentOwner}
          value={`${locationSelected?.ownerName} ${locationSelected?.ownerLastName} - (${locationSelected?.ownerEmail})`}
        />
      </Grid>
      <Grid container justifyContent="center" marginTop={1} marginBottom={2}>
        <ButtonStyledText
          onClick={() => setShowDetails(!showDetails)}
          variant="text"
          endIcon={
            showDetails ? (
              <ContainerArrow>
                <KeyboardArrowUpIcon />
              </ContainerArrow>
            ) : (
              <ContainerArrow>
                <KeyboardArrowDownIcon />
              </ContainerArrow>
            )
          }
        >
          {Strings.applications.showMoreDetails}
        </ButtonStyledText>
      </Grid>
      <ApplicationsDetails
        collapse={showDetails}
        openRightSideBarForMethod={openRightSideBarForMethod}
        makeDefaultLocation={makeDefaultLocationHandler}
        deleteLocation={deleteLocationHandler}
        resetLocation={resetLocationHandler}
      />
      <RightSideBar
        open={openRightSideBar}
        onMenuClose={() => {
          setOpenRightSideBar(false);
        }}
      >
        {switchComponentToShowRightSideBar()}
      </RightSideBar>
    </div>
  );
};
